import React from "react";
import styled, { keyframes } from "styled-components";
import { styles } from "../styles/themes/style";
import Text from "../components/Text";

type GearProps = {
    size: number;
    color: string;
    bgColor: string;
    className?: string;
};

const GearSvg: React.FC<GearProps> = ({ size, color, bgColor, className }) => {
    // Simple gear built from rotated teeth + ring; bgColor "cuts out" the inner hole.
    return (
        <svg
            className={className}
            width={size}
            height={size}
            viewBox="0 0 100 100"
            role="img"
            aria-label="Maintenance gear"
            focusable="false"
        >
            <g fill={color}>
                <rect x="46" y="2" width="8" height="20" rx="2" />
                <rect x="46" y="2" width="8" height="20" rx="2" transform="rotate(30 50 50)" />
                <rect x="46" y="2" width="8" height="20" rx="2" transform="rotate(60 50 50)" />
                <rect x="46" y="2" width="8" height="20" rx="2" transform="rotate(90 50 50)" />
                <rect x="46" y="2" width="8" height="20" rx="2" transform="rotate(120 50 50)" />
                <rect x="46" y="2" width="8" height="20" rx="2" transform="rotate(150 50 50)" />
                <rect x="46" y="2" width="8" height="20" rx="2" transform="rotate(180 50 50)" />
                <rect x="46" y="2" width="8" height="20" rx="2" transform="rotate(210 50 50)" />
                <rect x="46" y="2" width="8" height="20" rx="2" transform="rotate(240 50 50)" />
                <rect x="46" y="2" width="8" height="20" rx="2" transform="rotate(270 50 50)" />
                <rect x="46" y="2" width="8" height="20" rx="2" transform="rotate(300 50 50)" />
                <rect x="46" y="2" width="8" height="20" rx="2" transform="rotate(330 50 50)" />
                <circle cx="50" cy="50" r="34" />
            </g>

            {/* Cutout ring */}
            <circle cx="50" cy="50" r="24" fill={bgColor} />
            <circle cx="50" cy="50" r="10" fill={color} opacity="0.12" />
        </svg>
    );
};

const Maintenance: React.FC = () => {
    const bg = styles.colors.black100;
    const primary = styles.colors.primary600;

    return (
        <Screen $bg={bg}>
            <Content>
                <Gears aria-hidden="true">
                    <GearA size={110} color={primary} bgColor={bg} />
                    <GearB size={76} color={primary} bgColor={bg} />
                </Gears>
                <Text $heading2 $bold>
                    Maintenance in progress
                </Text>
                <Text $paragraph1 $faded>
                    We’re updating LeadDelta. Please check back in a few minutes.
                </Text>
            </Content>
        </Screen>
    );
};

const rotateCW = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const rotateCCW = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(-360deg); }
`;

const Screen = styled.div<{ $bg: string }>`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $bg }) => $bg};
  padding: 24px;
`;

const Content = styled.div`
  width: 100%;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
`;

const Gears = styled.div`
  position: relative;
  width: 190px;
  height: 130px;
  margin-bottom: 10px;
`;

const GearA = styled(GearSvg)`
  position: absolute;
  left: 16px;
  top: 10px;
  transform-origin: 50% 50%;
  animation: ${rotateCW} 2.8s linear infinite;
  filter: drop-shadow(0px 10px 18px rgba(51, 57, 81, 0.18));
`;

const GearB = styled(GearSvg)`
  position: absolute;
  left: 98px;
  top: 54px;
  transform-origin: 50% 50%;
  animation: ${rotateCCW} 2.2s linear infinite;
  filter: drop-shadow(0px 10px 18px rgba(51, 57, 81, 0.14));
`;

export default Maintenance;

